import { useCallback } from 'react';
import { dev } from '@breath-lab/domain';
import { logEvent } from '../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from './analytics';
import { SITE_BASE_URL, BREATHWORK_COUNT } from '../constants';

export const REFERRAL_KEY = 'breathlab_ref';
export const HOME_NUDGE_KEY = 'breathlab_share_nudge_dismissed';
export const SHARE_MILESTONES = [3, 7, 14, 30] as const;

export function captureReferralParam(refUid: string | null, currentUserUid: string | null): void {
  if (!refUid) return;
  if (currentUserUid) return;
  if (localStorage.getItem(REFERRAL_KEY)) return;
  localStorage.setItem(REFERRAL_KEY, refUid);
}

export function captureReferralOnSignup(): string | null {
  const uid = localStorage.getItem(REFERRAL_KEY);
  if (!uid) return null;
  localStorage.removeItem(REFERRAL_KEY);
  return uid;
}

export type ContentType = 'breathwork' | 'journey' | 'app';

export interface SharePayload {
  title: string;
  text: string;
  url: string;
}

export function buildSharePayload(
  contentType: ContentType,
  contentId: string,
  name: string,
  currentUserUid: string | null,
): SharePayload {
  const ref = currentUserUid ? `?ref=${currentUserUid}` : '';
  const base = SITE_BASE_URL;

  if (contentType === 'breathwork') {
    return {
      title: `${name} — Breath Lab`,
      text: `Try ${name} on Breath Lab — a free pranayama technique.`,
      url: `${base}/breathworks/${contentId}${ref}`,
    };
  }
  if (contentType === 'journey') {
    return {
      title: `${name} journey — Breath Lab`,
      text: `Try the ${name} journey on Breath Lab — free to explore.`,
      url: `${base}/journeys/${contentId}${ref}`,
    };
  }
  return {
    title: 'Breath Lab',
    text: `A pranayama app with ${BREATHWORK_COUNT} traditional breathing techniques.`,
    url: `${base}${ref}`,
  };
}

const NUDGE_TTL_MS = 20 * 24 * 60 * 60 * 1000; // 20 days

export function shouldShowMilestoneNudge(streak: number): boolean {
  if (!(SHARE_MILESTONES as readonly number[]).includes(streak)) return false;
  const dismissed = localStorage.getItem(`${HOME_NUDGE_KEY}_${streak}`);
  return !dismissed;
}

export function dismissMilestoneNudge(streak: number): void {
  localStorage.setItem(`${HOME_NUDGE_KEY}_${streak}`, '1');
}

export function isShareNudgeDismissed(): boolean {
  const raw = localStorage.getItem(HOME_NUDGE_KEY);
  if (!raw) return false;
  const dismissedAt = parseInt(raw, 10);
  return Date.now() - dismissedAt < NUDGE_TTL_MS;
}

export function dismissShareNudge(): void {
  localStorage.setItem(HOME_NUDGE_KEY, String(Date.now()));
}

export function useShare(
  source: 'detail' | 'success' | 'home',
  contentType: ContentType,
  contentId: string,
  name: string,
  currentUserUid: string | null,
) {
  return useCallback(async () => {
    const payload = buildSharePayload(contentType, contentId, name, currentUserUid);

    logEvent(AnalyticsEvent.shareInitiated, {
      [AnalyticsParam.source]: source,
      [AnalyticsParam.contentType]: contentType,
      [AnalyticsParam.id]: contentId,
    });

    if (navigator.share) {
      try {
        await navigator.share(payload);
      } catch (e) {
        if ((e as DOMException).name !== 'AbortError') {
          await copyFallback(payload.url);
        }
      }
    } else {
      await copyFallback(payload.url);
    }
  }, [source, contentType, contentId, name, currentUserUid]);
}

async function copyFallback(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    logEvent(AnalyticsEvent.shareCopiedFallback, {});
  } catch (e) {
    dev.warn('[share] clipboard write failed', e);
  }
}
